import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Trophy } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";

export default function PointsRulesSection() {
  const { activeWorkspaceId } = useAuth();
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(true);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(`/workspaces/${activeWorkspaceId}/points-rules`);
      setRows(data || []);
    } catch (e) {
      if (e?.response?.status === 403) toast.error("Only owner/admin can view points rules");
    } finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [activeWorkspaceId]);

  const patchLocal = (id, p) => setRows((c) => c.map((r) => r.id === id ? { ...r, ...p } : r));
  const save = async (rule, payload) => {
    try {
      await api.patch(`/points-rules/${rule.id}`, payload);
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); load(); }
  };

  return (
    <div className="border border-border p-6 space-y-4" data-testid="settings-points-rules">
      <div className="flex items-center gap-2">
        <Trophy className="w-4 h-4 text-primary" />
        <div className="label-eyebrow">Points engine</div>
      </div>
      <p className="text-sm">
        These rules drive the Leaderboard automatically. Tweak the points awarded/deducted for each qualifying activity, or toggle a rule off entirely. You can also manually award or deduct points from any teammate on the Leaderboard page.
      </p>
      {busy && rows.length === 0 ? (
        <div className="text-sm">Loading…</div>
      ) : (
        <div className="border border-border divide-y divide-border" data-testid="points-rules-list">
          {rows.map((r) => (
            <div key={r.id} className="px-4 py-3 grid grid-cols-12 gap-3 items-center" data-testid={`points-rule-${r.event_kind}`}>
              <div className="col-span-7 min-w-0">
                <div className="font-semibold">{r.label}</div>
                <div className="text-xs">{r.description}</div>
              </div>
              <div className="col-span-3">
                <label className="block label-eyebrow text-[10px] mb-1">Points</label>
                <Input
                  type="number"
                  value={r.points_delta}
                  onChange={(e) => patchLocal(r.id, { points_delta: Number(e.target.value) })}
                  onBlur={(e) => save(r, { points_delta: Number(e.target.value) })}
                  className="h-8 text-sm font-mono w-24"
                  data-testid={`points-delta-${r.event_kind}`}
                />
              </div>
              <div className="col-span-2 flex flex-col gap-1 items-end">
                <label className="label-eyebrow text-[10px]">{r.enabled ? "Enabled" : "Disabled"}</label>
                <Switch
                  checked={r.enabled}
                  onCheckedChange={(v) => { patchLocal(r.id, { enabled: v }); save(r, { enabled: v }); }}
                  data-testid={`points-enabled-${r.event_kind}`}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
